import React from 'react';
import { AdditionalInfoDTO as AdditionalInfoType } from '../../api/useEstatePlanningFlowQuery';
import {
  Popover,
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../../../../CommonComponents/OPL/Popover/Popover';
import { IconButton } from '../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import { Separator } from '../../../../../../../../CommonComponents/OPL/Separator/Separator';
import { Alert } from '../../../../../../../../CommonComponents/OPL/Alert/Alert';

type Variant = 'primary' | 'subtleInactive' | 'warning';

interface Props {
  additionalInfo: AdditionalInfoType;
  variant?: Variant;
}

export const AdditionalInfo = ({ additionalInfo, variant = 'subtleInactive' }: Props) => {
  return (
    <Popover>
      <PopoverTrigger>
        <IconButton
          aria-label="Submenu"
          icon={`far ${
            additionalInfo.showObligationsExceedLiquidAssetsWarning
              ? 'fa-exclamation-triangle'
              : 'fa-question-circle'
          }`}
          variant={additionalInfo.showObligationsExceedLiquidAssetsWarning ? 'warning' : variant}
          size="small"
          shape="circle"
          ml="8"
        />
      </PopoverTrigger>

      <PopoverContent width={384} p="16">
        {additionalInfo.showObligationsExceedLiquidAssetsWarning && (
          <Alert
            variant="warning"
            title="Estate Obligations Exceed Liquid Assets"
            message="The obligations of the estate, which include administration expenses, tax liabilities or other outstanding liability balances exceed the available balance of liquid assets"
          />
        )}

        <Text fontWeight={500} mb="16" textTransform="capitalize">
          {additionalInfo.title}
        </Text>

        <Box as="ul" css="list-style: none" p="0">
          {additionalInfo.description.map((info, infoIndex) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={infoIndex}>
                {info.title && (
                  <Text mb="16" fontWeight={500} textTransform="capitalize">
                    {info.title}
                  </Text>
                )}

                {info.calculations.map((calculation, calculationIndex) => {
                  return (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={calculationIndex}
                      as="li"
                      display="flex"
                      justifyContent="space-between"
                      mb="16"
                    >
                      <Text color="neutral.500" textTransform="capitalize">
                        {calculation.name}
                      </Text>

                      <Text variant="tabular" color="neutral.500">
                        {calculation.isSubtracted
                          ? `(${formatCurrency(calculation.value)})`
                          : formatCurrency(calculation.value)}
                      </Text>
                    </Box>
                  );
                })}

                {info.result && (
                  <Box as="li" display="flex" justifyContent="space-between" mb="16">
                    <Text fontWeight={500} textTransform="capitalize">
                      {info.result.name}
                    </Text>

                    <Text variant="tabular" fontWeight={500}>
                      {formatCurrency(info.result.value)}
                    </Text>
                  </Box>
                )}

                {additionalInfo.description.length > 1 &&
                  infoIndex + 1 !== additionalInfo.description.length && <Separator mb="16" />}
              </React.Fragment>
            );
          })}

          {additionalInfo.explanation && (
            <Text fontSize="small" color="neutral.500">
              {additionalInfo.explanation}
            </Text>
          )}
        </Box>

        <PopoverClose />

        <PopoverArrow />
      </PopoverContent>
    </Popover>
  );
};
